import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  faqs,
  howItWorks,
  CheckoutTerms,
  RegistrationTerms,
  RockiesMenuItems,
  CheckoutSuccessDescription,
} from "./helpers";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const LogoImage = "https://ss-leagues.s3.amazonaws.com/MLB-3/COL-2.png";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/MLB/Rockies%20Ballpark%20Assets/rockies-groups-banner.jpg";
const BackgroundImage =
  "https://ss-leagues.s3.amazonaws.com/MLB/Rockies%20Ballpark%20Assets/player-ballpark-pass-header-3-1920x1080.jpg";

const config: TeamConfig = {
  teamSchema: "coloradorockies_groups",
  browserTitle: "Rockies Groups",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Colorado Rockies",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10189-rockies-sec",
    mapId: "main",
    isGroupTicketSeatMapEnabled: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.groupLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 20,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 20,
    checkoutTimerEnabled: false,

    isTicketTransferEnabled: true,
    hideViewTicketsInThankYouPage: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/corockies/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: BackgroundImage,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: BackgroundImage,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    chooseSeatsPreloaderTexts: [
      { text: "Fetching available seats…", duration: 3000 },
      { text: "Huddle up— loading your group's options…", duration: 3000 },
      { text: "Almost there—gathering seat availability…", duration: 3000 },
    ],

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:3037625437"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (303) 762-5437
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#333366",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <RockiesMenuItems />,
    checkoutTermsAndCondition: <CheckoutTerms />,
    registrationTermsAndCondition: <RegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Select Continue to view seating options.",
      purchaseSeatsLanguageDisclaimer: (
        <>
          Not seeing exactly what you want? Contact us at{" "}
          <Link
            href="tel:3037625437"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            (303) 762-5437
          </Link>{" "}
          for more seating options.
        </>
      ),
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: <CheckoutSuccessDescription />,

    gameFilters: {
      seatCounter: {
        description:
          "Select the number of people in you group. Minimum of 20 required",
      },
      priceRange: {
        title: "Price Range per Ticket",
      },
      accessibility: {
        description:
          "If you are looking for accessible seating for your group, please contact the box office at (303) 762-5437.",
      },
    },

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
